/** Guards */

import type { Route } from '@angular/router';
import { authenticationGuard, authorizationChildGuard } from './core/auth/guards';
import { maintenanceGuard } from './core/auth/guards/maintenance.guard';
import { layoutResolver } from './layout/containers/layout/layout.resolve';
import { flagsmithFeatureFlagsGuard } from './shared/guards/flagsmith-feature-flags.guard';
import { globalGuard } from './shared/guards/global.guard';
import { userGuard } from './shared/guards/user.guard';
import { userAccountStatusGuard } from './shared/guards/user-account-status.guard';
import { SERVICE_NOW_LAUNCH_BASE_PATH } from './shared/models/service-now.interface';
export const APP_ROUTES: Route[] = [
  // redirects from `/app` to `/` for backwards compatibility
  {
    path: 'app',
    redirectTo: '',
  },
  {
    path: 'account-link',
    canActivate: [maintenanceGuard],
    loadChildren: () => import('./account-link/account-link.routes').then((m) => m.ACCOUNT_LINK_ROUTES),
  },
  {
    path: 'cookieJar',
    canActivate: [authenticationGuard, userAccountStatusGuard, userGuard, maintenanceGuard],
    pathMatch: 'full',
    loadComponent: () =>
      import('./shared/components/cookie-jar/cookie-jar.component').then((m) => m.CookieJarComponent),
  },
  {
    path: `${SERVICE_NOW_LAUNCH_BASE_PATH}/:page/:custID`,
    canActivate: [authenticationGuard, userAccountStatusGuard, userGuard, maintenanceGuard],
    pathMatch: 'full',
    loadComponent: () =>
      import('./shared/components/service-now-launch/service-now-launch.component').then(
        (m) => m.ServiceNowLaunchComponent
      ),
  },
  {
    path: 'maintenance-active',
    canActivate: [authenticationGuard, maintenanceGuard],
    loadComponent: () =>
      import('./shared/components/maintenance-active/maintenance-active.component').then(
        (m) => m.MaintenanceActiveComponent
      ),
  },
  {
    path: '',
    loadComponent: () => import('./layout/containers/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [
      authenticationGuard,
      userAccountStatusGuard,
      userGuard,
      globalGuard,
      flagsmithFeatureFlagsGuard,
      maintenanceGuard,
    ],
    canActivateChild: [authenticationGuard, authorizationChildGuard],
    pathMatch: 'prefix',
    children: [
      {
        path: '',
        loadChildren: () => import('./dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
      },
      {
        path: 'administration',
        loadChildren: () => import('./administration/administration.routes').then((m) => m.ADMINISTRATION_ROUTES),
      },
      {
        path: 'preferences',
        loadChildren: () => import('./preferences/preferences.routes').then((m) => m.PREFERENCES_ROUTES),
      },
      {
        path: 'dealer/prospects',
        loadChildren: () =>
          import('./dealer/dealer-prospects/dealer-prospects.routes').then((m) => m.DEALER_PROSPECTS_ROUTES),
      },
      {
        path: 'toolbox',
        loadChildren: () => import('./toolbox/toolbox.routes').then((m) => m.TOOLBOX_ROUTES),
      },
      {
        path: 'finance',
        loadChildren: () => import('./finance/finance.routes').then((m) => m.FINANCE_ROUTES),
      },
      {
        path: 'business',
        loadChildren: () => import('./business/business.routes').then((m) => m.BUSINESS_ROUTES),
      },
      {
        path: 'help',
        loadChildren: () => import('./help/help.routes').then((m) => m.HELP_ROUTES),
      },
      {
        path: 'tools',
        loadChildren: () => import('./tools/tools.routes').then((m) => m.TOOLS_ROUTES),
      },
      {
        path: 'customerReview',
        loadChildren: () => import('./customer-review/customer-review.routes').then((m) => m.CUSTOMER_REVIEW_ROUTES),
      },
      {
        path: 'salesManagement',
        loadChildren: () => import('./sales-management/sales-management.routes').then((m) => m.SALES_MANAGEMENT_ROUTES),
      },
      {
        path: 'product-visibility-management',
        loadChildren: () =>
          import('./product-visibility-management/product-visibility-management.routes').then(
            (m) => m.PRODUCT_VISIBILITY_MANAGEMENT_ROUTES
          ),
      },
      {
        path: 'somethingWrong',
        loadComponent: () =>
          import('./shared/components/something-wrong/something-wrong.component').then(
            (m) => m.SomethingWrongComponent
          ),
        data: {
          title: 'Something Went Wrong',
        },
      },
      // REDIRECTS for Top-Level Modules
      //  - used to be '/prospects', now it lives under '/dealer'
      // test URL (https://local.beckshybrids.com:4200/app/prospects/manageApplication) - redirect to (https://local.beckshybrids.com:4200/app/dealer/prospects/manageApplication)
      {
        path: 'prospects/manageApplication',
        redirectTo: '/dealer/prospects/manageApplication',
      },
      // test URL (https://local.beckshybrids.com:4200/app/prospects/13171) - redirect to (https://local.beckshybrids.com:4200/app/dealer/prospects/13171)
      {
        path: 'prospects/:prospectID',
        redirectTo: '/dealer/prospects/:prospectID',
        pathMatch: 'prefix',
      },
      // test URL (https://local.beckshybrids.com:4200/app/prospects) - redirect to (https://local.beckshybrids.com:4200/app/dealer/prospects)
      {
        path: 'prospects',
        redirectTo: '/dealer/prospects',
      },
      // Business Tier redirects
      {
        path: 'dealer/businessPlan',
        redirectTo: 'business/dealer/businessPlan',
      },
      {
        path: 'dealer/inventory',
        redirectTo: 'business/dealer/inventory',
      },
      {
        path: 'customer',
        redirectTo: 'business/customer',
      },
      // default catch-all redirect
      {
        path: '**',
        redirectTo: '/somethingWrong',
      },
    ],
    data: {
      title: 'BOSSng',
    },
    resolve: {
      layoutResolver,
    },
  },
  {
    path: 'errorOccurred',
    loadComponent: () =>
      import('./shared/components/something-wrong/something-wrong.component').then((m) => m.SomethingWrongComponent),
  },
];
